import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

interface DatePickerProps {
  selected?: Date | null;
  selectedDate?: Date | null;
  value?: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean; // Added for compatibility with other form components
}

const DatePicker: React.FC<DatePickerProps> = ({
  selected,
  selectedDate,
  value,
  onChange,
  label,
  error,
  disabled = false,
  minDate,
  maxDate,
  className = '',
  placeholder,
  fullWidth, // Ignored since the date picker container has w-full by default
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const effectiveValue = selected || value || selectedDate;
  const [displayedMonth, setDisplayedMonth] = useState(effectiveValue || new Date());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format date as string (MM/DD/YYYY)
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString();
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if a date is the selected date
  const isSelectedDate = (date: Date): boolean => {
    if (!effectiveValue) return false;
    
    return date.getDate() === effectiveValue.getDate() &&
           date.getMonth() === effectiveValue.getMonth() &&
           date.getFullYear() === effectiveValue.getFullYear();
  };

  // Check if a date is within the allowed range
  const isInRange = (date: Date): boolean => {
    let isValid = true;
    
    if (minDate) {
      // Set time to beginning of day for comparison
      const minDateCopy = new Date(minDate);
      minDateCopy.setHours(0, 0, 0, 0);
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);
      
      isValid = isValid && dateCopy >= minDateCopy;
    }
    
    if (maxDate) {
      // Set time to end of day for comparison
      const maxDateCopy = new Date(maxDate);
      maxDateCopy.setHours(23, 59, 59, 999);
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);
      
      isValid = isValid && dateCopy <= maxDateCopy;
    }
    
    return isValid;
  };

  // Get the days in the month
  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get the day of the week for the first day of the month
  const getFirstDayOfMonth = (month: number, year: number): number => {
    return new Date(year, month, 1).getDay();
  };

  // Navigate to the previous month
  const goToPrevMonth = (): void => {
    setDisplayedMonth(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() - 1, 1));
  };

  // Navigate to the next month
  const goToNextMonth = (): void => {
    setDisplayedMonth(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1));
  };

  // Handle date selection
  const handleDateSelect = (day: number): void => {
    const newDate = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), day);
    
    if (isInRange(newDate)) {
      onChange(newDate);
      setIsOpen(false);
    }
  };

  // Generate calendar days
  const generateCalendarDays = (): JSX.Element[] => {
    const month = displayedMonth.getMonth();
    const year = displayedMonth.getFullYear();
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    
    const days: JSX.Element[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-8 w-8"></div>
      );
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const inRange = isInRange(date);
      
      days.push(
        <button
          type="button"
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={!inRange || disabled}
          className={`h-8 w-8 rounded-full flex items-center justify-center text-sm transition-colors
            ${isSelectedDate(date) ? 'bg-red text-white' : ''}
            ${isToday(date) && !isSelectedDate(date) ? 'border border-red text-red dark:text-red-400' : ''}
            ${!inRange || disabled ? 'text-text-tertiary dark:text-text-tertiary cursor-not-allowed' : 'hover:bg-background-tertiary dark:hover:bg-background-tertiary text-text-primary dark:text-text-primary'}
          `}
        >
          {day}
        </button>
      );
    }
    
    return days;
  };

  // Day names for the calendar header
  const dayNames = [
    t('common.daysShort.sun'),
    t('common.daysShort.mon'),
    t('common.daysShort.tue'),
    t('common.daysShort.wed'),
    t('common.daysShort.thu'),
    t('common.daysShort.fri'),
    t('common.daysShort.sat')
  ];

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-text-secondary dark:text-text-primary mb-1">
          {label}
        </label>
      )}
      
      <div ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-3 py-2 border ${
            error ? 'border-error' : 'border-border-medium dark:border-border-heavy'
          } rounded shadow-sm bg-background-primary text-text-primary text-left focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue transition-colors ${
            disabled ? 'bg-background-tertiary text-text-tertiary cursor-not-allowed' : ''
          }`}
        >
          {effectiveValue ? formatDate(effectiveValue) : (placeholder || 'Select date')}
        </button>
        
        {isOpen && (
          <div className="absolute mt-1 w-72 bg-background-primary dark:bg-background-secondary shadow-lg rounded border border-border-light dark:border-border-medium z-10">
            <div className="p-3">
              {/* Calendar header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPrevMonth}
                  className="p-1 hover:bg-background-tertiary dark:hover:bg-background-tertiary rounded-full transition-colors"
                  aria-label="Previous month"
                >
                  <svg className="w-5 h-5 text-text-secondary dark:text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="font-medium text-text-primary dark:text-text-primary">
                  {displayedMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </div>

                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-1 hover:bg-background-tertiary dark:hover:bg-background-tertiary rounded-full transition-colors"
                  aria-label="Next month"
                >
                  <svg className="w-5 h-5 text-text-secondary dark:text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              {/* Day names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="h-8 flex items-center justify-center text-xs text-text-tertiary dark:text-text-secondary">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {generateCalendarDays()}
              </div>
              
              {/* Today button */}
              <div className="mt-4 text-right">
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    if (isInRange(today)) {
                      onChange(today);
                      setIsOpen(false);
                    }
                  }}
                  disabled={!isInRange(new Date()) || disabled}
                >
                  Today
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
    </div>
  );
};

export default DatePicker;